import { nText } from "../../i18n/notificationAdminPresentation";
import type { NotificationItem } from "../../hooks/useNotificationsFeed";
import type { TFunction } from "i18next";

/**
 * Format a notification timestamp for display.
 * Accepts unix epoch seconds (number) from the DB-backed API.
 */
export const formatTimestamp = (value: number, locale?: string): string => {
  const ms = value * 1000;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale);
};

export const typeColor: Record<
  NotificationItem["type"],
  "default" | "muted" | "primary" | "success" | "warning" | "danger"
> = {
  general: "default",
  app: "primary",
  support: "muted",
  system: "warning",
  security: "danger",
  session: "success",
};

export const notificationTypeLabels: Record<NotificationItem["type"], string> = {
  get general() { return nText("general"); },
  get app() { return nText("applications"); },
  get support() { return nText("support"); },
  get system() { return nText("system"); },
  get security() { return nText("security"); },
  get session() { return nText("sessions"); },
};

export const notificationTypeOptions = Object.keys(notificationTypeLabels).map((value) => ({
  value,
  get label() { return notificationTypeLabels[value as NotificationItem["type"]]; },
}));

export const getNotificationTypeLabels = (t: TFunction<"notifications">): Record<NotificationItem["type"], string> => ({
  general: t(($) => $.general),
  app: t(($) => $.applications),
  support: t(($) => $.support),
  system: t(($) => $.system),
  security: t(($) => $.security),
  session: t(($) => $.sessions),
});

export const getNotificationTypeLabel = (value: NotificationItem["type"], t?: TFunction<"notifications">) =>
  (t ? getNotificationTypeLabels(t) : notificationTypeLabels)[value] ?? value;

/**
 * Strip markdown formatting tokens for safe plain-text display in table cells.
 * Removes bold/italic markers, code fences, links, headers, list bullets, callout prefixes.
 */
export const stripMarkdown = (text: string): string =>
  text
    // Remove GFM alert/callout prefixes like > [!NOTE], > [!WARNING] etc.
    .replace(/>\s*\[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)]\s*/gi, "")
    // Remove fenced code blocks (```...```)
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code backticks
    .replace(/`([^`]+)`/g, "$1")
    // Remove images ![alt](url)
    .replace(/!\[([^\]]*?)]\([^)]*?\)/g, "$1")
    // Remove links [text](url)
    .replace(/\[([^\]]*?)]\([^)]*?\)/g, "$1")
    // Remove bold/italic markers
    .replace(/(\*{1,3}|_{1,3})(.+?)\1/g, "$2")
    // Remove heading markers
    .replace(/^#{1,6}\s+/gm, "")
    // Remove blockquote markers
    .replace(/^>\s?/gm, "")
    // Remove list bullets
    .replace(/^[-*+]\s+/gm, "")
    // Remove numbered list markers
    .replace(/^\d+\.\s+/gm, "")
    // Remove horizontal rules
    .replace(/^---+$/gm, "")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
